import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {AccountModel} from 'src/app/shared/models';
import {
  AccountAzureSsoService,
  AccountEmail2faService,
  AuthService,
  Broadcaster,
  DataService,
  ToasterService
} from 'src/app/shared/services';

@Component({
  selector: 'app-account-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss']
})
export class AuthAccountListComponent implements OnInit {

  items: AccountModel[] = [];
  isLoading = false;
  isBusy = false;
  isSsoBusy = false;
  isEmail2faBusy = false;
  email2faModalOpen = false;
  email2faStep: 'send' | 'verify' = 'send';
  email2faMaskedEmail = '';
  email2faError: string | null = null;
  email2faResendDisabled = false;
  private pendingEmail2faAccount: AccountModel | null = null;

  constructor(
    public auth: AuthService,
    public router: Router,
    private route: ActivatedRoute,
    public data: DataService,
    public broadcast: Broadcaster,
    private accountAzureSso: AccountAzureSsoService,
    private accountEmail2fa: AccountEmail2faService,
    private toastr: ToasterService
  ) {
  }

  ngOnInit(): void {
    this.initPage();
  }

  async initPage(): Promise<void> {
    await this.resetToken();
    await this.loadItems();
    await this.handleSsoCallback();
  }

  async resetToken(): Promise<void> {
    const response = await this.data.post(
      'api/users/reset-token',
      {}
    ).toPromise();
    this.auth.saveUser(response);
  }

  async loadItems(): Promise<void> {
    this.isLoading = true;
    try {
      const accounts = await this.data.get(
        'api/access/accounts'
      ).toPromise();
      this.items = (accounts || []).map((account: AccountModel) => new AccountModel(account));
    } finally {
      this.isLoading = false;
    }
  }

  async handleSsoCallback(): Promise<void> {
    const params = this.route.snapshot.queryParamMap;
    const sso = params.get('sso');
    if (!sso) {
      return;
    }

    const accountId = params.get('accountId');
    const message = params.get('message');

    await this.clearSsoQueryParams();

    if (sso === 'error') {
      this.toastr.showError(message || 'navigations.account.sso_error', !!message);
      return;
    }

    if (sso !== 'success' || !accountId) {
      return;
    }

    const account = this.items.find((item) => String(item.id) === accountId);
    if (!account) {
      this.toastr.showError('navigations.account.sso_error');
      return;
    }

    await this.enterAccount(account);
  }

  async clearSsoQueryParams(): Promise<void> {
    await this.router.navigate(['/accounts'], {replaceUrl: true});
  }

  requiresEmail2fa(account: AccountModel): boolean {
    return !!account.email2faEnabled && !account.ssoEnabled;
  }

  async setAccount(account: AccountModel): Promise<void> {
    if (this.isBusy || this.isLoading) {
      return;
    }

    if (account.ssoEnabled) {
      await this.ensureSsoAndEnter(account);
      return;
    }

    if (this.requiresEmail2fa(account)) {
      await this.ensureEmail2faAndEnter(account);
      return;
    }

    await this.enterAccount(account);
  }

  async ensureEmail2faAndEnter(account: AccountModel): Promise<void> {
    this.isBusy = true;
    this.isEmail2faBusy = true;
    this.email2faError = null;

    try {
      const status = await this.accountEmail2fa.status(account.id).toPromise();
      if (status?.verified) {
        await this.enterAccount(account);
        return;
      }

      this.pendingEmail2faAccount = account;
      this.email2faMaskedEmail = status?.maskedEmail || '';
      this.email2faStep = 'send';
      this.email2faResendDisabled = false;
      this.email2faModalOpen = true;
    } catch (error) {
      this.handleEmail2faError(error);
    } finally {
      this.isBusy = false;
      this.isEmail2faBusy = false;
    }
  }

  async onEmail2faSend(): Promise<void> {
    const account = this.pendingEmail2faAccount;
    if (!account || this.isEmail2faBusy) {
      return;
    }

    this.isEmail2faBusy = true;
    this.email2faError = null;

    try {
      const start = await this.accountEmail2fa.start(account.id).toPromise();
      if (!start?.sent) {
        this.toastr.showError('navigations.account.email_2fa_error');
        return;
      }

      this.email2faMaskedEmail = start.maskedEmail || this.email2faMaskedEmail;
      this.email2faStep = 'verify';
      this.email2faResendDisabled = true;
      setTimeout(() => {
        this.email2faResendDisabled = false;
      }, 60000);
    } catch (error) {
      this.handleEmail2faError(error, true);
    } finally {
      this.isEmail2faBusy = false;
    }
  }

  async onEmail2faVerify(code: string): Promise<void> {
    const account = this.pendingEmail2faAccount;
    if (!account || this.isEmail2faBusy) {
      return;
    }

    this.isEmail2faBusy = true;
    this.email2faError = null;

    try {
      await this.accountEmail2fa.verify(account.id, code).toPromise();
      this.closeEmail2faModal();
      await this.enterAccount(account);
    } catch (error) {
      this.handleEmail2faError(error, true);
    } finally {
      this.isEmail2faBusy = false;
    }
  }

  async onEmail2faResend(): Promise<void> {
    const account = this.pendingEmail2faAccount;
    if (!account || this.isEmail2faBusy || this.email2faResendDisabled) {
      return;
    }

    this.isEmail2faBusy = true;
    this.email2faError = null;

    try {
      const start = await this.accountEmail2fa.start(account.id).toPromise();
      if (!start?.sent) {
        this.toastr.showError('navigations.account.email_2fa_error');
        return;
      }

      this.email2faMaskedEmail = start.maskedEmail || this.email2faMaskedEmail;
      this.email2faResendDisabled = true;
      setTimeout(() => {
        this.email2faResendDisabled = false;
      }, 60000);
    } catch (error) {
      this.handleEmail2faError(error, true);
    } finally {
      this.isEmail2faBusy = false;
    }
  }

  closeEmail2faModal(): void {
    this.email2faModalOpen = false;
    this.email2faStep = 'send';
    this.pendingEmail2faAccount = null;
    this.email2faMaskedEmail = '';
    this.email2faError = null;
    this.email2faResendDisabled = false;
  }

  async ensureSsoAndEnter(account: AccountModel, forceStart = false): Promise<void> {
    this.isBusy = true;
    this.isSsoBusy = true;
    try {
      const ssoService = this.resolveSsoService(account.provider);
      if (!ssoService) {
        this.toastr.showError('navigations.account.sso_error');
        return;
      }

      if (!forceStart) {
        const status = await ssoService.status(account.id).toPromise();
        if (status?.verified) {
          await this.enterAccount(account);
          return;
        }
      }

      const start = await ssoService.start(account.id).toPromise();
      if (start?.authorizationUrl) {
        window.location.href = start.authorizationUrl;
        return;
      }

      this.toastr.showError('navigations.account.sso_error');
    } catch (error) {
      this.handleError(error);
    } finally {
      this.isBusy = false;
      this.isSsoBusy = false;
    }
  }

  private resolveSsoService(provider: string | null): AccountAzureSsoService | null {
    switch (provider) {
      case 'azure':
        return this.accountAzureSso;
      default:
        return null;
    }
  }

  async enterAccount(account: AccountModel): Promise<void> {
    this.isBusy = true;
    try {
      const token = await this.data.post(
        'api/users/ut',
        {account}
      ).toPromise();
      this.auth.clearSection();
      this.auth.clearState(['fpm_models_tasks', 'fpm_models_tasks_from_models']);
      this.auth.saveUser(token);
      await this.router.navigate(['/sites']);
    } catch (error) {
      if (this.isVerificationRequired(error) && account.ssoEnabled) {
        this.isBusy = false;
        await this.ensureSsoAndEnter(account, true);
        return;
      }
      if (this.isVerificationRequired(error) && this.requiresEmail2fa(account)) {
        this.isBusy = false;
        await this.ensureEmail2faAndEnter(account);
        return;
      }
      this.handleError(error);
    } finally {
      this.isBusy = false;
    }
  }

  private isVerificationRequired(error: any): boolean {
    return error?.status === 403;
  }

  private handleEmail2faError(error: any, inModal = false): void {
    const message = error?.error?.message || error?.message;
    if (inModal && message) {
      this.email2faError = message;
      return;
    }
    if (message) {
      this.toastr.showError(message, true);
      return;
    }
    this.toastr.showError('navigations.account.email_2fa_error');
  }

  private handleError(error: any): void {
    const message = error?.error?.message || error?.message;
    if (message) {
      this.toastr.showError(message, true);
      return;
    }
    this.toastr.showError('navigations.account.sso_error');
  }

}
